import type { Action, BoundActions } from 'react-sweet-state';
import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';

import type { AiIssueCreateState } from '@atlassian/ai-issue-create';
import { AvailableSite } from '@atlassian/gic-anywhere';

import type { InsertStorageFragmentRequestPayload } from './__types__/InsertStorageFragmentRequestPayload';
import { JiraIssue, JiraProject } from './__types__/apiUtils';

export interface IssueType {
	iconUrl: string;
	name: string;
	id: string;
}

export interface IssueObject {
	id: string;
	summary: string;
	description?: DocNode;
	issueType: IssueType;
}

// Still not fully known what the exact object shape that will be sent to us is
export type AiBulkIssuesListType = IssueObject[];

export type BulkCreateContextType = {
	aiBulkIssuesList: AiBulkIssuesListType;
	insertStorageFragmentRequestPayload?: InsertStorageFragmentRequestPayload;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	currentJiraSite: AvailableSite;
	currentJiraProject: JiraProject;
	currentIssueId: string;
};

type State = {
	aiBulkIssuesList: AiBulkIssuesListType;
	currentIssueId: string;
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	currentJiraSite: AvailableSite | undefined;
	currentJiraProject: JiraProject | undefined;
	currentDefaultIssueType: JiraIssue | undefined;
};

export type IssueCreateSidePanelActions = BoundActions<State, typeof bulkCreateActions>;

const initialBulkCreateState: State = {
	aiBulkIssuesList: [],
	currentIssueId: '',
	insertStorageFragmentRequestPayload: undefined,
	aiState: 'idle',
	isCreatedWithAi: false,
	currentJiraSite: undefined,
	currentJiraProject: undefined,
	currentDefaultIssueType: undefined,
};

const bulkCreateActions = {
	setCurrentIssueId:
		(currentIssueId: State['currentIssueId']): Action<State> =>
		({ setState }) => {
			setState({ currentIssueId });
		},
	setAiBulkIssuesList:
		(aiBulkIssuesList: State['aiBulkIssuesList']): Action<State> =>
		({ setState }) => {
			setState({ aiBulkIssuesList });
		},
	setAiState:
		(aiState: State['aiState']): Action<State> =>
		({ setState }) => {
			setState({ aiState });
		},
	setIsCreatedWithAi:
		(isCreatedWithAi: State['isCreatedWithAi']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedWithAi });
		},
	setInsertStorageFragmentRequestPayload:
		(insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload): Action<State> =>
		({ setState }) => {
			setState({ insertStorageFragmentRequestPayload });
		},
	removeAiBulkIssue:
		(issueId: string): Action<State> =>
		({ setState, getState }) => {
			const { aiBulkIssuesList } = getState();
			setState({ aiBulkIssuesList: aiBulkIssuesList.filter((issue) => issue.id !== issueId) });
		},
	setCurrentJiraSite:
		(currentJiraSite: State['currentJiraSite']): Action<State> =>
		({ setState }) => {
			setState({ currentJiraSite });
		},
	setCurrentJiraProject:
		(currentJiraProject: State['currentJiraProject']): Action<State> =>
		({ setState }) => {
			setState({ currentJiraProject });
		},
	setCurrentDefaultIssueType:
		(currentDefaultIssueType: State['currentDefaultIssueType']): Action<State> =>
		({ setState }) => {
			setState({ currentDefaultIssueType });
		},
};

const Store = createStore({
	initialState: {
		...initialBulkCreateState,
	},
	actions: bulkCreateActions,
	name: 'bulkCreateContext',
});

export const useBulkCreateContext = createHook(Store);

const useActions = createActionsHook(Store);

export const useSetIsCreatedWithAi = () => {
	const { setIsCreatedWithAi } = useActions();
	return setIsCreatedWithAi;
};

export const useSetCurrentIssueId = () => {
	const { setCurrentIssueId } = useActions();
	return setCurrentIssueId;
};
